import * as React from 'react';
import type { OmitId, DataModel, DataSource } from './types';

import { useNotifications } from '../useNotifications';

type FormState<D extends DataModel> = {
  values: Partial<OmitId<D>>;
  errors: Partial<Record<keyof D, string>>;
};

export function useFormValidation<D extends DataModel>(
  { fields, validate }: Pick<DataSource<D>, 'fields' | 'validate'>,
  setFormState: React.Dispatch<React.SetStateAction<FormState<D>>>
) {
  const notifications = useNotifications();
  const validationNotificationKey = React.useId();
  const validationVersion = React.useRef(0);
  const resetVersion = React.useRef(0);
  const pendingValidationFields = React.useRef(new Set<string>());
  const resetValidation = React.useCallback(() => {
    ++validationVersion.current;
    ++resetVersion.current;
    pendingValidationFields.current.clear();
  }, []);

  React.useEffect(() => resetValidation, [resetValidation]);

  const validateField = React.useCallback(
    async (name: keyof D, values: Partial<OmitId<D>>) => {
      if (!validate) return;
      const version = ++validationVersion.current;
      const affectedFields = new Set([
        String(name),
        ...(fields.find((field) => field.field === name)
          ?.validationDependencies ?? []),
      ]);
      for (const field of affectedFields)
        pendingValidationFields.current.add(field);
      try {
        const { issues } = await validate(values);
        // The latest full-form result also refreshes fields queued by superseded runs.
        if (version !== validationVersion.current) return;
        const refreshedFields = Array.from(pendingValidationFields.current);
        setFormState((previousState) => ({
          ...previousState,
          errors: {
            ...previousState.errors,
            ...Object.fromEntries(
              refreshedFields.map((field) => [
                field,
                issues?.find((issue) => issue.path?.[0] === field)?.message,
              ])
            ),
          },
        }));
      } catch {
        if (version === validationVersion.current) {
          notifications.show('Unable to validate form. Please try again.', {
            severity: 'error',
            key: validationNotificationKey,
          });
        }
      } finally {
        if (version === validationVersion.current)
          pendingValidationFields.current.clear();
      }
    },
    [fields, validate, setFormState, notifications, validationNotificationKey]
  );

  const validateForm = React.useCallback(
    async (values: Partial<OmitId<D>>) => {
      resetValidation();
      const version = validationVersion.current;
      const reset = resetVersion.current;
      const isSuperseded = () => {
        if (version === validationVersion.current) return false;
        // Resets and unmounts cancel validation without notifying a discarded form.
        if (reset === resetVersion.current) {
          notifications.show(
            'Form changed during validation. Please save again.',
            { severity: 'warning', key: validationNotificationKey }
          );
        }
        return true;
      };
      try {
        const result = validate ? await validate(values) : undefined;
        if (isSuperseded()) return false;
        const issues = result?.issues ?? [];
        const formIssues = issues.filter(
          (issue) => issue.path?.[0] === undefined
        );
        if (formIssues.length) {
          notifications.show(
            formIssues.map((issue) => issue.message).join('\n'),
            {
              severity: 'error',
            }
          );
        }
        setFormState((previous) => ({
          ...previous,
          errors: Object.fromEntries(
            issues
              .filter((issue) => issue.path?.[0] !== undefined)
              .map((issue) => [issue.path?.[0], issue.message])
          ),
        }));
        return !result?.issues?.length;
      } catch {
        if (isSuperseded()) return false;
        notifications.show('Unable to validate form. Please try again.', {
          severity: 'error',
          key: validationNotificationKey,
        });
        return false;
      }
    },
    [
      resetValidation,
      validate,
      setFormState,
      notifications,
      validationNotificationKey,
    ]
  );

  return { validateForm, validateField, resetValidation };
}
